import React, { useState, useEffect, useRef } from 'react';
import { 
  Shield,
  QrCode,
  Copy,
  Download,
  Share2,
  RefreshCw,
  AlertTriangle,
  Check,
  Loader2,
  Info,
  Trash2,
  Clock,
  Mail,
  FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../services/api';
import { QRCodeSVG } from 'qrcode.react';
import jsPDF from 'jspdf';

const EmergencyAccess = () => {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [hasActiveCode, setHasActiveCode] = useState(false);
  const [code, setCode] = useState(null);
  const [codeCreatedAt, setCodeCreatedAt] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  const [copied, setCopied] = useState(false);
  const [denyingRequestId, setDenyingRequestId] = useState(null);
  const qrRef = useRef(null);

  // App URL for emergency access
  const emergencyAccessUrl = code 
    ? `${window.location.origin}/emergency?code=${code}` 
    : null;

  // Fetch status on mount
  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/emergency-access/status');
      if (response.data.success) {
        setHasActiveCode(response.data.hasActiveCode);
        setCode(response.data.code);
        setCodeCreatedAt(response.data.codeCreatedAt);
        setPendingRequests(response.data.pendingRequests || []);
      }
    } catch (error) {
      console.error('Failed to fetch emergency access status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCode = async () => {
    try {
      setGenerating(true);
      const response = await api.post('/api/emergency-access/generate');
      if (response.data.success) {
        setHasActiveCode(true);
        setCode(response.data.code);
        setCodeCreatedAt(response.data.created_at);
        toast.success(response.data.is_existing 
          ? 'Your existing code is shown below' 
          : 'Emergency access code generated!'
        );
      }
    } catch (error) {
      toast.error('Failed to generate code');
    } finally {
      setGenerating(false);
    }
  };

  const handleRevokeCode = async () => {
    try {
      const response = await api.post('/api/emergency-access/revoke');
      if (response.data.success) {
        setHasActiveCode(false);
        setCode(null);
        setCodeCreatedAt(null);
        setShowRevokeDialog(false);
        toast.success('Emergency access code revoked');
      }
    } catch (error) {
      toast.error('Failed to revoke code');
    }
  };

  const handleDenyRequest = async (requestId) => {
    try {
      setDenyingRequestId(requestId);
      const response = await api.post(`/api/emergency-access/deny-request/${requestId}`);
      if (response.data.success) {
        // Remove the denied request from the list
        setPendingRequests(prev => prev.filter(r => r.id !== requestId));
        // Code has been invalidated — reset the code state
        setHasActiveCode(false);
        setCode(null);
        setCodeCreatedAt(null);
        toast.success('Access request denied. The requester has been notified and your emergency code has been invalidated. Generate a new code when ready.');
      } else {
        toast.error(response.data.error || 'Failed to deny request');
      }
    } catch (error) {
      console.error('Failed to deny request:', error);
      toast.error('Failed to deny request');
    } finally {
      setDenyingRequestId(null);
    }
  };

  const handleCopyCode = () => {
    if (code) {
      navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success('Code copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyUrl = () => {
    if (emergencyAccessUrl) {
      navigator.clipboard.writeText(emergencyAccessUrl);
      toast.success('URL copied to clipboard');
    }
  };

  const handleDownloadQR = async () => {
    if (!emergencyAccessUrl || !qrRef.current) return;
    
    try {
      // Get the SVG element from the QR code
      const svgElement = qrRef.current.querySelector('svg');
      if (!svgElement) {
        toast.error('QR code not found');
        return;
      }
      
      // Create a canvas and draw the SVG on it
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const img = new Image();
      
      // Set canvas size (larger for better quality)
      canvas.width = 400;
      canvas.height = 400;
      
      img.onload = () => {
        // Fill white background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        // Draw QR code
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Download as PNG
        const link = document.createElement('a');
        link.download = `lifely-emergency-access-${code}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        
        toast.success('QR code downloaded');
      };
      
      img.onerror = () => {
        toast.error('Failed to generate QR code image');
      };
      
      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download QR code');
    }
  };

  const handleShare = async () => {
    if (!emergencyAccessUrl) return;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Lifely Emergency Access',
          text: `Emergency access code: ${code}`,
          url: emergencyAccessUrl,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          handleCopyUrl();
        }
      }
    } else {
      handleCopyUrl();
    }
  };

  const handlePrintPDF = async () => {
    if (!emergencyAccessUrl || !qrRef.current) return;

    try {
      const svgElement = qrRef.current.querySelector('svg');
      if (!svgElement) {
        toast.error('QR code not found');
        return;
      }

      // Convert SVG to PNG data URL via canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 400;
      canvas.height = 400;
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const img = new Image();

      img.onload = () => {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const qrDataUrl = canvas.toDataURL('image/png');

        // Build PDF
        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 20;
        let y = 20;

        // Header bar
        doc.setFillColor(74, 58, 46);
        doc.rect(0, 0, pageWidth, 14, 'F');
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text('Lifely - lifely.guide', margin, 9);
        doc.setTextColor(0, 0, 0);

        y = 28;

        // Title
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.text('Emergency Access Card', margin, y);
        y += 12;

        // Subtitle
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(`Generated: ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`, margin, y);
        doc.setTextColor(0, 0, 0);
        y += 14;

        // Important notice box
        doc.setFillColor(255, 248, 235);
        doc.setDrawColor(217, 119, 6);
        doc.roundedRect(margin, y, pageWidth - margin * 2, 38, 3, 3, 'FD');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(146, 64, 14);
        doc.text('Keep this document safe. Handle with care.', margin + 5, y + 8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(92, 45, 5);
        const noticeLines = doc.splitTextToSize(
          'This card is intended for a trusted person, someone you would want to have access to your personal information in the event you are no longer able to manage your affairs yourself. This code is SINGLE-USE and will be invalidated after one request. Store it with your will, in a sealed envelope, or in another secure location. Only share it with someone you completely trust.',
          pageWidth - margin * 2 - 10
        );
        doc.text(noticeLines, margin + 5, y + 16);
        doc.setTextColor(0, 0, 0);
        y += 46;

        // QR Code + Code side by side
        doc.setFillColor(250, 250, 250);
        doc.setDrawColor(220, 220, 220);
        doc.roundedRect(margin, y, pageWidth - margin * 2, 75, 3, 3, 'FD');

        // QR code image
        doc.addImage(qrDataUrl, 'PNG', margin + 8, y + 8, 58, 58);

        // Code details to the right
        const rightX = margin + 75;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text('Access Code', rightX, y + 15);
        doc.setFontSize(28);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(74, 58, 46);
        doc.text(code, rightX, y + 30);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text('Emergency Access URL', rightX, y + 45);
        doc.setFontSize(7.5);
        doc.setTextColor(0, 0, 0);
        const urlLines = doc.splitTextToSize(emergencyAccessUrl, pageWidth - margin - rightX - 5);
        doc.text(urlLines, rightX, y + 53);
        doc.setTextColor(0, 0, 0);
        y += 83;

        // How it works section
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('How to use this card', margin, y);
        y += 8;

        const steps = [
          '1.  If you need to request emergency access to this person\'s Lifely information, visit the URL above or scan the QR code.',
          '2.  Enter the access code when prompted and provide your name, email, and reason for requesting access.',
          '3.  The account holder will receive a notification and has 48 hours to deny the request.',
          '4.  If there is no response within 48 hours, access will be granted as part of their continuity plan.',
          '5.  IMPORTANT: This code is single-use. Once submitted, it cannot be used again. The account holder must generate a new code if needed.',
        ];

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        steps.forEach(step => {
          const lines = doc.splitTextToSize(step, pageWidth - margin * 2);
          doc.text(lines, margin, y);
          y += lines.length * 5 + 3;
        });

        y += 6;

        // Footer note
        doc.setFillColor(245, 245, 245);
        doc.rect(margin, y, pageWidth - margin * 2, 20, 'F');
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(120, 120, 120);
        const footerText = 'Lifely is a privacy-first platform for mapping assets and intentions. This document was generated by the account holder and is for authorised use only. Misuse of emergency access codes is a breach of trust and may have legal consequences.';
        const footerLines = doc.splitTextToSize(footerText, pageWidth - margin * 2 - 6);
        doc.text(footerLines, margin + 3, y + 6);

        // Page footer
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text('Generated by Lifely (lifely.guide)', margin, pageHeight - 10);

        doc.save(`lifely-emergency-access-${code}.pdf`);
        toast.success('PDF saved, ready to print');
      };

      img.onerror = () => toast.error('Failed to generate PDF');
      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    } catch (error) {
      console.error('PDF error:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="emergency-access">
      {/* Header */}
      <div className="space-y-2">
        <h2 className="text-xl font-heading font-semibold">Emergency Access</h2>
        <p className="text-sm text-muted-foreground max-w-2xl">
          Generate an emergency access code that someone can use if you become unresponsive. 
          They'll need to verify their request, and you'll have 48 hours to deny it.
        </p>
      </div>

      {/* Info Notice */}
      <div className="flex gap-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
        <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800 dark:text-amber-200">
          <p className="font-medium">How it works</p>
          <ol className="mt-2 space-y-1 text-amber-700 dark:text-amber-300">
            <li>1. Generate a code and share it with a trusted person (e.g., store with your will)</li>
            <li>2. If something happens, they visit the emergency access page and enter the code</li>
            <li>3. You receive an email to verify you're okay - click "Deny" if it's not legitimate</li>
            <li>4. If you don't respond within 48 hours, your continuity process is triggered</li>
          </ol>
          <p className="mt-3 font-medium text-amber-800 dark:text-amber-200">Important: Each code is single-use</p>
          <p className="mt-1 text-amber-700 dark:text-amber-300">
            Once a code is used to submit a request (whether approved or denied), it is permanently invalidated. 
            You will need to generate a new code afterwards. This prevents the code from being forwarded or reused by unauthorised parties.
          </p>
        </div>
      </div>

      {/* Main Card */}
      <Card className="border border-border">
        <CardContent className="p-6">
          {!hasActiveCode ? (
            /* No Code State */
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-lg font-medium mb-2">No Emergency Access Code</h3>
              <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                Generate a code to allow trusted people to request access to your information in an emergency.
              </p>
              <Button onClick={handleGenerateCode} disabled={generating}>
                {generating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <QrCode className="w-4 h-4 mr-2" />
                )}
                Generate Emergency Code
              </Button>
            </div>
          ) : (
            /* Has Code State */
            <div className="space-y-6">
              {/* Code Display */}
              <div className="flex flex-col sm:flex-row gap-6 items-center">
                {/* QR Code */}
                <div className="shrink-0" ref={qrRef}>
                  <div className="p-3 bg-white rounded-lg border border-border shadow-sm">
                    <QRCodeSVG 
                      value={emergencyAccessUrl}
                      size={150}
                      level="M"
                      includeMargin={false}
                    />
                  </div>
                </div>

                {/* Code & Actions */}
                <div className="flex-1 text-center sm:text-left">
                  <p className="text-xs text-muted-foreground mb-1">Your emergency access code</p>
                  <div className="flex items-center gap-2 justify-center sm:justify-start mb-3">
                    <code className="text-3xl font-mono font-bold tracking-widest text-primary">
                      {code}
                    </code>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleCopyCode}
                      className="h-8 w-8 p-0"
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Created: {formatDate(codeCreatedAt)}
                  </p>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 mt-4 justify-center sm:justify-start">
                    <Button variant="outline" size="sm" onClick={handlePrintPDF} data-testid="print-pdf-btn">
                      <FileText className="w-4 h-4 mr-2" />
                      Print PDF
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleDownloadQR}>
                      <Download className="w-4 h-4 mr-2" />
                      Save QR
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleShare}>
                      <Share2 className="w-4 h-4 mr-2" />
                      Share
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-destructive hover:text-destructive"
                      onClick={() => setShowRevokeDialog(true)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Revoke
                    </Button>
                  </div>
                </div>
              </div>

              {/* URL Display */}
              <div className="pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground mb-2">Emergency access URL</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-muted p-2 rounded truncate">
                    {emergencyAccessUrl}
                  </code>
                  <Button variant="ghost" size="sm" onClick={handleCopyUrl} className="shrink-0">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Tips */}
              <div className="pt-4 border-t border-border space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Keeping your code safe</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• Use <strong>Print PDF</strong> to create a card you can store with your will or important documents</li>
                  <li>• Only share with someone you deeply trust, a family member, close friend, or executor</li>
                  <li>• This code is <strong>single-use</strong> - once used for a request, it is permanently invalidated</li>
                  <li>• If a code is used (for any reason), you must generate a new one from this page</li>
                  <li>• You'll always receive an email notification and have 48 hours to deny any access request</li>
                </ul>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Requests */}
      {pendingRequests.length > 0 && (
        <Card className="border border-amber-300 bg-amber-50 dark:bg-amber-950/30">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-medium text-amber-800 dark:text-amber-200">
              <AlertTriangle className="w-4 h-4" />
              Pending Access Requests
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingRequests.map((request) => (
              <div key={request.id} className="p-3 rounded-lg bg-white dark:bg-background border border-amber-200 dark:border-amber-800">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{request.requesterName}</p>
                    <p className="text-xs text-muted-foreground">{request.requesterEmail}</p>
                    {request.reason && (
                      <p className="text-xs text-muted-foreground mt-1">Reason: {request.reason}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                      <Clock className="w-3 h-3 mr-1" />
                      Expires {formatDate(request.expiresAt)}
                    </Badge>
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => handleDenyRequest(request.id)}
                      disabled={denyingRequestId === request.id}
                    >
                      {denyingRequestId === request.id ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <Trash2 className="w-3 h-3 mr-1" />
                      )}
                      Deny
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Revoke Dialog */}
      <Dialog open={showRevokeDialog} onOpenChange={setShowRevokeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Revoke Emergency Access Code?
            </DialogTitle>
            <DialogDescription className="pt-2">
              This will invalidate your current code. Anyone with the old code will no longer be able to request access.
              You can generate a new code at any time.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowRevokeDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRevokeCode}>
              Revoke Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmergencyAccess;
